import { Icon } from 'components/icon/Icon';
import { Tooltip } from 'components/tooltip/Tooltip';
import { Icons } from 'contents/icons';
import React, { useEffect, useRef, useState } from 'react';
import { DropDownListOptionType } from 'types/common/CommonType';
import { DropdownList } from '../DropdownList/DropdownList';
import { StyledSelectBox } from './style';

/**
 * 텍스트 입력필드 컴포넌트 속성
 */
interface SelectBoxProps {
  size: 'large' | 'medium' | 'small';
  value?: DropDownListOptionType;
  options: DropDownListOptionType[];
  width?: string;
  maxWidth?: string;
  label?: string;
  className?: string;
  icon?: keyof typeof Icons;
  infoDetail?: string;
  placeholder?: string;
  helpMessage?: string;
  folderList?: DropDownListOptionType[];
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  comboBox?: boolean;
  skipOption?: boolean;
  enableAddOption?: boolean;
  enableFontSelection?: boolean;
  searchExclude?: string[];
  handleValue: (value: DropDownListOptionType) => void;
  handleBlurValue?: (value?: DropDownListOptionType) => void;
  handleFocusValue?: (value?: DropDownListOptionType) => void;
  onClick?: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseUp?: (e: React.MouseEvent) => void;
  onMount?: () => void;
  onUnmount?: () => void;
}

/**
 * SelectBox component
 * @author hc_yang
 * @param {SelectBoxProps} param
 * @returns JSX.Element
 */
export const SelectBox = ({
  size,
  value,
  options,
  width = '100%',
  maxWidth = '100%',
  label,
  className,
  icon,
  infoDetail,
  placeholder,
  helpMessage,
  folderList,
  required = false,
  readOnly = false,
  disabled = false,
  comboBox = false,
  skipOption = false,
  enableAddOption = false,
  enableFontSelection = false,
  searchExclude = [],
  handleValue,
  handleFocusValue,
  handleBlurValue,
  onClick,
  onMouseDown,
  onMouseUp,
  onMount,
  onUnmount,
}: SelectBoxProps) => {
  const PreIcon = icon && Icons[icon];
  const parentRef = useRef<HTMLDivElement | null>(null);
  const searchFieldParentRef = useRef<HTMLDivElement | null>(null);
  const infoRef = useRef<HTMLDivElement | null>(null);

  const [isInfoHovered, setIsInfoHovered] = useState(false);
  const [isListVisible, setIsListVisible] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<DropDownListOptionType | undefined>(
    folderList ? folderList.find((folder) => folder.default) || folderList[0] : undefined
  );

  const handleClick = () => {
    if (!readOnly && !disabled && !skipOption) setIsListVisible(true);
  };

  const handleSelectedValue = (selected: DropDownListOptionType) => {
    setIsListVisible(false);
    handleValue(selected);
  };

  useEffect(() => {
    if (isListVisible) handleFocusValue && handleFocusValue(value);
    else handleBlurValue && handleBlurValue(value);
  }, [isListVisible]);

  useEffect(() => {
    onMount && onMount();
    return () => {
      onUnmount && onUnmount();
    };
  }, []);

  return (
    <StyledSelectBox
      $size={size}
      $width={width}
      $maxWidth={maxWidth}
      $readOnly={readOnly}
      $disabled={disabled}
      className={className}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseOut={onMouseUp}
    >
      {label && (
        <div className="label-area">
          <p className="label">{label}</p>
          {required && <p className="required">*</p>}
          {infoDetail && (
            <div
              className="info"
              ref={infoRef}
              onMouseEnter={() => setIsInfoHovered(true)}
              onMouseLeave={() => setIsInfoHovered(false)}
            >
              <Icon
                icon="InfoCircle"
                size={10}
                style={{ padding: size === 'large' ? '0.1rem' : '0' }}
                stroke="--text-gray-4"
              />
              {isInfoHovered && <Tooltip parentRef={infoRef} direction="left" label={infoDetail} />}
            </div>
          )}
        </div>
      )}
      <div className="form-wrap">
        <div
          className={`form-area ${isListVisible ? 'active' : ''}`}
          onClick={handleClick}
          data-focus="true"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleClick()}
          ref={parentRef}
        >
          {PreIcon && <PreIcon className="icon" />}
          <p
            className="value"
            style={
              (enableFontSelection && {
                fontFamily: value?.value as string,
                fontWeight: 400,
              }) ||
              undefined
            }
            ref={searchFieldParentRef}
          >
            {value?.value !== undefined && value?.value !== null && value?.value !== '' ? (
              value.label
            ) : (
              <span className="placeholder">{placeholder}</span>
            )}
          </p>
          {!readOnly && (
            <button className="dropdown-btn" tabIndex={-1}>
              {comboBox ? (
                <Icons.SearchLg className="icon search-icon" />
              ) : (
                <Icons.ChevronDown className="icon chevron-icon" />
              )}
            </button>
          )}
          {isListVisible && (
            <DropdownList
              parentRef={parentRef}
              type="select"
              size={size}
              options={options}
              value={value}
              handleValue={handleSelectedValue}
              isSearchOption={comboBox}
              folderList={folderList}
              folderValue={selectedFolder}
              searchExclude={searchExclude}
              handleFolder={setSelectedFolder}
              handleListClose={() => setIsListVisible(false)}
              searchFieldParentRef={searchFieldParentRef}
              enableFontSelection={enableFontSelection}
              maxHeight={size === 'large' ? '28rem' : size === 'medium' ? '24rem' : '20rem'}
            />
          )}
        </div>
      </div>

      {helpMessage && (
        <div className="info-area">
          <p className="help">{helpMessage}</p>
        </div>
      )}
    </StyledSelectBox>
  );
};
